/* A group's polls.
 *
 * "Polls" is one of the six checkboxes in the client's Features block.
 *
 * RESULTS ARE ALWAYS VISIBLE, including before you vote. Hiding them until you
 * answer is a way of stopping people being influenced, but this is a church
 * group deciding which Saturday suits — seeing that eleven people picked the
 * 13th is the entire point.
 *
 * Voting again moves your vote rather than adding one: the server keys votes on
 * (poll, person), so the total cannot double-count however often somebody
 * changes their mind.
 */
import { useState } from 'react'
import { BarChart3, Loader2, Plus, Trash2, X } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import { Field } from '@/components/ui/field'
import { Modal } from '@/components/ui/modal'
import {
  useCreatePoll,
  useDeletePoll,
  useGroupPolls,
  useCan,
  useMe,
  useVote,
  useVoters,
} from '@/api/queries'
import { cn } from '@/lib/utils'
import type { Poll } from '@/types/api'

export function PollsPanel({
  churchId,
  groupId,
}: {
  churchId: string
  groupId: string
}) {
  const [adding, setAdding] = useState(false)
  const polls = useGroupPolls(churchId, groupId)

  // Same rule as events: the endpoint enforces polls.create.
  const can = useCan(churchId, groupId)

  return (
    <div className="space-y-s3">
      {can('polls.create') ? (
        <div className="flex justify-end">
          <Button variant="primary" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            New poll
          </Button>
        </div>
      ) : null}

      {polls.isLoading ? (
        <div className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </div>
      ) : null}

      {!polls.isLoading && !polls.data?.length ? (
        <Card className="p-s8 text-center">
          <BarChart3 className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
          <p className="mt-s3 text-md font-bold">No polls yet</p>
          <p className="mt-1 text-base text-ink-2">
            Ask the group a question and see what they say.
          </p>
        </Card>
      ) : null}

      {polls.data?.map((p) => (
        <PollCard key={p.id} poll={p} churchId={churchId} groupId={groupId} />
      ))}

      {adding ? (
        <NewPollModal
          churchId={churchId}
          groupId={groupId}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </div>
  )
}

function PollCard({
  poll,
  churchId,
  groupId,
}: {
  poll: Poll
  churchId: string
  groupId: string
}) {
  const vote = useVote(churchId, groupId)
  const remove = useDeletePoll(churchId, groupId)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [showWho, setShowWho] = useState(false)
  const { data: me } = useMe()
  const can = useCan(churchId, groupId)

  const closed = poll.closes_at ? new Date(poll.closes_at) < new Date() : false
  const mine =
    // PERMISSION FIRST, OWNERSHIP SECOND.
    //
    // polls.create is the gate: the poll belongs to the GROUP, so somebody
    // who made it as a moderator and was demoted can no longer remove it.
    //
    // Then whose it is — and posts.moderate ("Delete anyone's post" on the
    // Roles screen) lifts that, which is the whole point of the permission.
    // Ownership is compared BY ID: two members called "Sujay G" would each
    // have seen a delete button on the other's poll.
    can('polls.create') &&
    (can('posts.moderate') || poll.created_by_id === me?.id)

  return (
    <Card className="p-s4">
      <div className="flex items-start gap-s3">
        <div className="min-w-0 flex-1">
          <h3 className="text-md font-bold">{poll.question}</h3>
          <p className="mt-1 text-base text-ink-2">
            {poll.total_votes} {poll.total_votes === 1 ? 'vote' : 'votes'}
            {closed ? ' · closed' : ''}
          </p>
        </div>

        {mine ? (
          <button
            onClick={() => setConfirmDelete(true)}
            aria-label={`Delete poll: ${poll.question}`}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-pill text-ink-2 transition-colors hover:bg-hover-2 hover:text-danger"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
      </div>

      <Confirm
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this poll?"
        body="Every vote cast on it goes too. This cannot be undone."
        confirmLabel="Delete poll"
        loading={remove.isPending}
        onConfirm={() => remove.mutate(poll.id)}
      />

      <ul className="mt-s3 space-y-s2">
        {poll.options.map((o) => {
          const chosen = poll.my_option_id === o.id
          // Share of the vote, for the bar behind the label. Guarded against
          // the zero-vote case, where 0/0 would be NaN and the bar would vanish.
          const pct = poll.total_votes
            ? Math.round((o.votes / poll.total_votes) * 100)
            : 0
          return (
            <li key={o.id}>
              <button
                onClick={() =>
                  !closed && vote.mutate({ pollId: poll.id, optionId: o.id })
                }
                disabled={closed || vote.isPending}
                aria-pressed={chosen}
                className={cn(
                  'relative w-full overflow-hidden rounded-md border px-s3 py-s2 text-left transition-colors',
                  chosen
                    ? 'border-brand bg-brand-softer'
                    : 'border-line hover:bg-hover',
                  closed && 'cursor-default',
                )}
              >
                {/* The bar sits BEHIND the text rather than beside it, so a
                    long option is never squeezed by the chart. */}
                <span
                  className={cn(
                    'absolute inset-y-0 left-0 transition-all',
                    chosen ? 'bg-brand/20' : 'bg-surface-2',
                  )}
                  style={{ width: `${pct}%` }}
                  aria-hidden
                />
                <span className="relative flex items-center justify-between gap-s3">
                  <span
                    className={cn(
                      'min-w-0 break-words text-base',
                      chosen ? 'font-bold text-brand' : 'font-semibold',
                    )}
                  >
                    {o.label}
                  </span>
                  <span className="shrink-0 text-sm font-semibold text-ink-2">
                    {o.votes} · {pct}%
                  </span>
                </span>
              </button>
            </li>
          )
        })}
      </ul>

      <div className="mt-s2 flex flex-wrap items-center justify-between gap-s2">
        {!closed && poll.my_option_id ? (
          <p className="text-sm text-ink-2">
            Tap another option to change your vote.
          </p>
        ) : (
          <span />
        )}
        {/* Not a secret ballot — a group deciding which Saturday suits needs
            to know who has not answered. */}
        {poll.total_votes ? (
          <button
            onClick={() => setShowWho(true)}
            className="text-base font-semibold text-brand hover:underline"
          >
            See who voted
          </button>
        ) : null}
      </div>

      {showWho ? (
        <VoterList
          churchId={churchId}
          groupId={groupId}
          poll={poll}
          onClose={() => setShowWho(false)}
        />
      ) : null}
    </Card>
  )
}

/** Who voted, grouped under the option they chose. */
function VoterList({
  churchId,
  groupId,
  poll,
  onClose,
}: {
  churchId: string
  groupId: string
  poll: Poll
  onClose: () => void
}) {
  const voters = useVoters(churchId, groupId, poll.id)

  return (
    <Modal open onOpenChange={(next) => !next && onClose()} title={poll.question}>
      {voters.isLoading ? (
        <div className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </div>
      ) : null}

      {poll.options.map((o) => {
        const rows = (voters.data ?? []).filter((v) => v.option_id === o.id)
        // Every option is listed, including the ones nobody picked — "0 votes"
        // is information, and a missing row reads as a missing option.
        return (
          <div key={o.id}>
            <h3 className="border-b border-line-2 bg-surface-2 px-s4 py-s2 text-sm font-bold uppercase tracking-wide text-ink-2">
              {o.label} · {rows.length}
            </h3>
            {rows.length ? (
              <ul className="divide-y divide-line-2">
                {rows.map((v) => (
                  <li key={v.id} className="flex items-center gap-s3 px-s4 py-s3">
                    <Avatar name={v.name} src={v.avatar_url} size="sm" />
                    <span className="truncate text-md font-semibold">{v.name}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-s4 py-s3 text-base text-ink-2">Nobody yet.</p>
            )}
          </div>
        )
      })}
    </Modal>
  )
}

function NewPollModal({
  churchId,
  groupId,
  onClose,
}: {
  churchId: string
  groupId: string
  onClose: () => void
}) {
  const [question, setQuestion] = useState('')
  // Two blanks to start with, because a poll needs at least two answers and
  // an empty list makes that rule invisible.
  const [options, setOptions] = useState(['', ''])
  const create = useCreatePoll(churchId, groupId)

  const filled = options.map((o) => o.trim()).filter(Boolean)
  const ready = Boolean(question.trim()) && filled.length >= 2

  function setOption(i: number, value: string) {
    setOptions((prev) => prev.map((o, idx) => (idx === i ? value : o)))
  }

  function submit() {
    if (!ready || create.isPending) return
    create.mutate(
      { question: question.trim(), options: filled },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title="New poll"
      guardClose={Boolean(question.trim() || filled.length)}
      footer={
        <Button
          variant="primary"
          size="lg"
          full
          onClick={submit}
          disabled={!ready}
          loading={create.isPending}
        >
          {ready ? 'Create poll' : 'Add a question and two options'}
        </Button>
      }
    >
      <div className="space-y-s3 p-s4">
        <Field
          id="poll-question"
          label="Question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Which Saturday suits everyone?"
          autoFocus
          required
        />

        {options.map((o, i) => (
          <div key={i} className="flex items-end gap-s2">
            <div className="flex-1">
              <Field
                id={`poll-option-${i}`}
                label={`Option ${i + 1}`}
                value={o}
                onChange={(e) => setOption(i, e.target.value)}
              />
            </div>
            {/* Removing is only offered above the minimum of two. */}
            {options.length > 2 ? (
              <button
                onClick={() =>
                  setOptions((prev) => prev.filter((_, idx) => idx !== i))
                }
                aria-label={`Remove option ${i + 1}`}
                className="mb-1.5 grid h-9 w-9 shrink-0 place-items-center rounded-pill text-ink-2 transition-colors hover:bg-hover-2 hover:text-danger"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            ) : null}
          </div>
        ))}

        {options.length < 10 ? (
          <Button
            variant="secondary"
            onClick={() => setOptions((prev) => [...prev, ''])}
          >
            <Plus className="h-4 w-4" aria-hidden />
            Add option
          </Button>
        ) : null}

        {create.isError ? (
          <p className="text-base text-danger">
            That did not save. Check the question and options, then try again.
          </p>
        ) : null}
      </div>
    </Modal>
  )
}
